import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, Chrome, Twitter, Gamepad2 } from 'lucide-react';
import KolMyGlassLogo from '../KolMyGlassLogo';

export interface LoginFormProps {
  onLogin: (email: string, password: string, remember: boolean) => void | Promise<void>;
  onSocialLogin?: (provider: string) => void;
  isLoading?: boolean;
  error?: string | null;
  /** Pre-filled email (e.g. from “Remember me”) */
  defaultEmail?: string;
  /** When false, hides OAuth buttons (driven by backend /api/config/public in production). */
  socialLoginEnabled?: boolean;
}

interface FormInputProps {
  icon: React.ReactNode;
  type: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  id?: string;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  title?: string;
  inputClassName?: string;
}

interface SocialButtonProps {
  icon: React.ReactNode;
  name: string;
  onClick?: () => void;
  disabled?: boolean;
}

interface ToggleSwitchProps {
  checked: boolean;
  onChange: () => void;
  id: string;
}

export const FormInput: React.FC<FormInputProps> = ({
  icon,
  type,
  placeholder,
  value,
  onChange,
  id,
  name,
  required,
  disabled,
  readOnly,
  title,
  inputClassName = '',
}) => {
  return (
    <div className="relative">
      <div className="absolute left-3 top-1/2 z-[1] -translate-y-1/2 pointer-events-none">{icon}</div>
      <input
        id={id}
        name={name}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        title={title}
        className={`glass-input w-full pl-10 pr-3 py-2.5 transition-colors disabled:opacity-60 ${readOnly ? 'cursor-default opacity-90' : ''} ${inputClassName}`}
      />
    </div>
  );
};

const SocialButton: React.FC<SocialButtonProps> = ({ icon, name, onClick, disabled }) => {
  return (
    <button
      type="button"
      aria-label={name}
      title={`Continue with ${name}`}
      onClick={onClick}
      disabled={disabled}
      className="btn-glass flex items-center justify-center p-2 opacity-90 hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {icon}
    </button>
  );
};

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ checked, onChange, id }) => {
  return (
    <label
      htmlFor={id}
      className="relative inline-block h-5 w-10 shrink-0 cursor-pointer rounded-full focus-within:outline-none focus-within:ring-2 focus-within:ring-accent2/55 focus-within:ring-offset-2 focus-within:ring-offset-white dark:focus-within:ring-offset-zinc-900"
    >
      <input type="checkbox" id={id} className="sr-only" checked={checked} onChange={onChange} />
      <span
        className={`absolute inset-0 rounded-full border-2 shadow-inner transition-colors duration-200 ease-in-out ${
          checked
            ? 'border-[rgb(var(--accent-rgb)/0.85)] bg-[rgb(var(--accent-rgb))]'
            : 'border-glass-text-3/50 bg-glass-2/90 dark:border-glass-text-3/40'
        }`}
        aria-hidden
      >
        <span
          className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full border border-glass-text/15 bg-white shadow-sm transition-transform duration-200 ease-in-out ${
            checked ? 'translate-x-5' : ''
          }`}
        />
      </span>
    </label>
  );
};

export const LoginForm: React.FC<LoginFormProps> = ({
  onLogin,
  onSocialLogin,
  isLoading = false,
  error,
  defaultEmail = '',
  socialLoginEnabled = true,
}) => {
  const [email, setEmail] = useState(defaultEmail);
  useEffect(() => {
    if (defaultEmail) setEmail(defaultEmail);
  }, [defaultEmail]);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onLogin(email, password, remember);
  };

  return (
    <div className="glass-card p-8 shadow-xl">
      <div className="mb-8 flex flex-col items-center text-center">
        <Link
          to="/"
          className="mb-3 outline-none focus-visible:rounded-lg focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent-rgb)/0.55)] focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-zinc-900"
        >
          <KolMyGlassLogo variant="hero" heroScale={0.6} />
        </Link>
        <p className="text-xs text-glass-text-3">Sign in to your workspace</p>
      </div>

      {error ? (
        <div className="mb-4 rounded-xl border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-600 dark:text-rose-200">
          {error}
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-6">
        <FormInput
          icon={<Mail className="text-glass-text-3" size={18} />}
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={isLoading}
        />

        <div className="relative">
          <FormInput
            icon={<Lock className="text-glass-text-3" size={18} />}
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
            inputClassName="pr-12"
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 z-[1] -translate-y-1/2 text-glass-text-3 transition-colors hover:text-glass-text focus:outline-none"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center space-x-2">
            <ToggleSwitch
              checked={remember}
              onChange={() => setRemember(!remember)}
              id="remember-me"
            />
            <span className="cursor-pointer text-sm text-glass-text-2 hover:text-glass-text" onClick={() => setRemember(!remember)}>
              Remember me
            </span>
          </div>
          <Link to="/forgot-password" className="shrink-0 text-sm text-accent2 transition-colors hover:underline">
            Forgot password
          </Link>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-accent w-full transform py-3 font-medium shadow-lg transition-all duration-200 ease-in-out hover:-translate-y-0.5 focus:outline-none disabled:transform-none disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isLoading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      {socialLoginEnabled ? (
        <div className="mt-8">
          <div className="relative flex items-center justify-center">
            <div className="absolute w-full border-t border-glass" />
            <span className="relative z-[1] rounded-full border border-glass bg-glass-2 px-3 py-1 text-xs text-glass-text-3">
              quick access via
            </span>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3">
            <SocialButton
              icon={<Chrome size={18} />}
              name="Google"
              onClick={() => onSocialLogin?.('google')}
              disabled={isLoading}
            />
            <SocialButton
              icon={<Twitter size={18} />}
              name="X"
              onClick={() => onSocialLogin?.('twitter')}
              disabled={isLoading}
            />
            <SocialButton
              icon={<Gamepad2 size={18} />}
              name="Discord"
              onClick={() => onSocialLogin?.('discord')}
              disabled={isLoading}
            />
          </div>
        </div>
      ) : null}

      <p className="mt-8 text-center text-sm text-glass-text-3">
        Don&apos;t have an account?{' '}
        <Link to="/register" className="font-medium text-accent2 transition-colors hover:underline">
          Create account
        </Link>
      </p>
    </div>
  );
};

export interface TwoFactorPanelProps {
  onVerify: (code: string) => void | Promise<void>;
  onBack: () => void;
  isLoading?: boolean;
  error?: string | null;
}

export const GamingTwoFactorPanel: React.FC<TwoFactorPanelProps> = ({
  onVerify,
  isLoading,
  error,
  onBack,
}) => {
  const [code, setCode] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onVerify(code);
  };

  return (
    <div className="glass-card p-8 shadow-xl">
      <div className="mb-6 flex justify-center">
        <Link
          to="/"
          className="outline-none focus-visible:rounded-lg focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent-rgb)/0.55)] focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-zinc-900"
        >
          <KolMyGlassLogo variant="nav" />
        </Link>
      </div>
      <h2 className="mb-2 text-center text-2xl font-bold tracking-tightest text-glass-text">Two-factor authentication</h2>
      <p className="mb-6 text-center text-sm text-glass-text-2">
        Enter the 6-digit code from your authenticator app, or a recovery code.
      </p>
      {error ? (
        <div className="mb-4 rounded-xl border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-600 dark:text-rose-200">
          {error}
        </div>
      ) : null}
      <form onSubmit={submit} className="space-y-4">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
          disabled={isLoading}
          placeholder="123456"
          className="glass-input w-full px-4 py-3 text-center text-lg tracking-widest disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={isLoading}
          className="btn-accent w-full py-3 font-medium disabled:opacity-60"
        >
          {isLoading ? 'Verifying…' : 'Verify and continue'}
        </button>
        <button
          type="button"
          onClick={onBack}
          className="w-full text-sm text-glass-text-3 hover:text-accent2"
        >
          Back to email and password
        </button>
      </form>
    </div>
  );
};

const GamingLogin = {
  LoginForm,
  GamingTwoFactorPanel,
};

export default GamingLogin;
